<script setup lang="ts">
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { cn } from '@/lib/utils'

import { computed, ref } from 'vue'

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    default: '',
  },
  class: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
  },
  error: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: ' ',
  },
  completion: {
    type: String,
    default: '',
  },

})

const emit = defineEmits(['update:modelValue', 'focus', 'blur'])

const modelValue = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const isFocused = ref(false)

const attrs = useAttrs()

const inputElement = ref<HTMLInputElement>()
defineExpose({ inputElement })
const textMeasure = ref<HTMLElement>()
const inputWidth = ref(0)
const completionLeft = ref(0)

watchEffect(() => {
  if (textMeasure.value && inputElement.value) {
    textMeasure.value.textContent = props.modelValue || ''
    inputWidth.value = textMeasure.value.offsetWidth
    completionLeft.value = inputWidth.value + 4
  }
})
function handleFocus(event: FocusEvent) {
  isFocused.value = true
  emit('focus', event)
}
function handleBlur(event: Event) {
  isFocused.value = false
  emit('blur', event)
}
</script>

<template>
  <div class="relative w-full shadow-sm rounded-md">
    <div ref="textMeasure" class="invisible absolute whitespace-nowrap pointer-events-none text-sm" />

    <!-- Main Input -->
    <slot name="input">
      <Input
        :id="id"
        ref="inputElement"
        v-model="modelValue"
        class="peer h-12 pb-0 pt-4 focus:bg-transparent transition-all z-10 text-foreground"
        :class="[
          error && 'bg-destructive/15 border-destructive focus:border-destructive focus:ring-destructive',
        ]"
        placeholder=" "
        :type="type"
        v-bind="attrs"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </slot>

    <div
      v-if="isFocused && !modelValue && placeholder"
      class="absolute top-1/2 pt-4 left-3 z-0 -translate-y-1/2 text-muted-foreground pointer-events-none text-sm line-clamp-1"
    >
      {{ placeholder }}
    </div>

    <!-- Floating Label -->
    <Label
      :for="id"
      :class="
        cn(
          'absolute w-full start-2 top-3 z-10 origin-[0] py-1.5 font-normal -translate-y-4 scale-[0.8] transform px-1 text-sm text-gray-600 peer-focus:text-black duration-300',
          'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100',
          'peer-focus:top-3 peer-focus:-translate-y-4 peer-focus:scale-[0.85] peer-focus:px-1',
          'rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4 cursor-text',
        )
      "
    >
      {{ label }}
    </Label>
  </div>
</template>
